"use client";

import { notFound, useParams } from "next/navigation";
import { useLocale } from "@/lib/context/LocaleProvider";
import { isApiError } from "@/lib/api/client";
import { fetchTool, mapApiToolDetail } from "@/lib/api/tools";
import type { Tool } from "@/lib/types";
import { useAsyncData } from "@/lib/hooks/useAsyncData";
import { useDocumentTitle } from "@/lib/hooks/useDocumentTitle";
import { useReportPageLoading } from "@/lib/hooks/useReportPageLoading";
import { BackButton } from "@/components/ui/BackButton";
import { ToolDetailSkeleton } from "@/components/ui/Skeleton";
import { ToolForm } from "@/components/forms/ToolForm";

/** GET /v1/tool/{slug} when the tool is missing. */
function isToolNotFoundError(err: unknown): boolean {
  return isApiError(err) && (err.status === 404 || err.code === "NOT_FOUND");
}

/**
 * Route-level fallback for /tools/[tool] — identical to the screen's own
 * loading branch, so the Suspense hand-off doesn't reflow the page.
 *
 * The frame carries no `animate-fade-up`: the fallback and the loaded screen
 * are separate trees, so React swaps the whole <section> when the boundary
 * resolves and the entrance animation would run a second time on the new node.
 * Only the ready branch below animates.
 */
export function ToolDetailScreenFallback() {
  const { t } = useLocale();
  return (
    <section className="px-5 pt-7">
      <div className="mx-auto max-w-[1024px]">
        <BackButton href="/tools" label={t.navTools} />
        <ToolDetailSkeleton />
      </div>
    </section>
  );
}

/** /tools/[tool] — tool detail with the generate form. */
export function ToolDetailScreen() {
  const params = useParams<{ tool: string }>();
  const slug = String(params.tool);
  const { t, locale, hydrated: localeHydrated } = useLocale();
  // Wait for locale from localStorage so we don't fetch with the default
  // and immediately refetch with the stored language.
  const requestKey = localeHydrated ? `${locale}|${slug}` : null;
  const { status, data: tool } = useAsyncData<Tool>(requestKey, async () => {
    try {
      return mapApiToolDetail(await fetchTool(slug, locale));
    } catch (err) {
      if (!isToolNotFoundError(err) && !isApiError(err)) {
        console.error(`Failed to load tool "${slug}"`, err);
      }
      throw err;
    }
  });

  // Any failure lands on the 404 page — the tool page has no partial state.
  const missing = status === "error";

  useReportPageLoading(!localeHydrated || status === "loading" || status === "idle");

  useDocumentTitle(tool?.name);

  const categoryTrail =
    tool?.category
      ? {
          label: tool.category,
          href: `/tools?category=${encodeURIComponent(
            tool.categorySlug ?? tool.category,
          )}`,
        }
      : undefined;

  if (!localeHydrated || status === "loading" || status === "idle") {
    return <ToolDetailScreenFallback />;
  }

  if (missing || !tool) notFound();

  return (
    <section className="animate-fade-up px-5 pt-7">
      <div className="mx-auto max-w-[1024px]">
        <BackButton href="/tools" label={t.navTools} trail={categoryTrail} />
        <ToolForm tool={tool} />
      </div>
    </section>
  );
}
